import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Type,
  ViewContainerRef,
} from '@angular/core'
import { Subject, Subscription } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  AdvancedOptionsBase,
  AdvancedOptionsParams,
} from '../../models/advanced-options.model'

@Component({
  selector: 'hs-advanced-options',
  template: '',
})
export class AdvancedOptionsComponent implements OnDestroy {
  private _advancedOptions?: Type<unknown> | null
  private _advancedOptionsParams?: AdvancedOptionsParams | null
  private _componentRef?: ComponentRef<AdvancedOptionsBase>
  private componentOnChangeSub?: Subscription

  @Input() set advancedOptions(value: Type<unknown> | null | undefined) {
    this._advancedOptions = value
    this.renderAdvancedOptions()
  }

  @Input() set advancedOptionsParams(
    value: AdvancedOptionsParams | null | undefined,
  ) {
    this._advancedOptionsParams = value
    this.setAdvancedOptionParams()
  }

  @Output() change = new EventEmitter<AdvancedOptionsParams>()

  get advancedOptionsInstance(): AdvancedOptionsBase | undefined {
    return this._componentRef?.instance
  }

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    private readonly resolver: ComponentFactoryResolver,
    private readonly container: ViewContainerRef,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this._componentRef?.destroy()
  }

  renderAdvancedOptions() {
    if (!this._advancedOptions) {
      return
    }

    const factory = this.resolver.resolveComponentFactory(this._advancedOptions)
    this._componentRef = this.container.createComponent(
      factory,
    ) as ComponentRef<AdvancedOptionsBase>

    if (this._advancedOptionsParams) {
      this.setAdvancedOptionParams()
    }
  }

  setAdvancedOptionParams() {
    if (
      !this._componentRef ||
      !this._advancedOptionsParams ||
      !this._componentRef.instance
    ) {
      return
    }

    const instance = this._componentRef.instance

    instance.advancedOptionsParams = this._advancedOptionsParams

    this.componentOnChangeSub?.unsubscribe()
    this.componentOnChangeSub = instance.onChange
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(params => {
        this.change.emit(params)
      })
  }

  onApply() {
    this._componentRef?.instance.onApply()
  }
}
